import { useEffect, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useFormContext, useWatch } from "react-hook-form";
import { t } from "ttag";

import { uploadTempFile } from "@/app/api/legacy-api";
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_UPLOAD_SIZE,
} from "@/shared/models/legacy-constants";
import type { UploadFilesValues } from "@/shared/models/legacy-types";
import {
  CheckIcon,
  DocumentIcon,
  IconWrapper,
  Input,
  Label,
  Message,
  ReloadIcon,
  Spinner,
  TrashBin2Icon,
  UploadedFileCard,
} from "@/shared/ui";
import { formatBytes, tw } from "@/shared/utils";
import {
  DOCUMENT_ICON,
  getDocumentEditNameLabel,
} from "../components.constants";

interface FileUploadProps {
  fieldIndex: number;
  onRemove: () => void;
}

export const FileUpload = ({ fieldIndex, onRemove }: FileUploadProps) => {
  const {
    formState: { errors },
    control,
    register,
    setError,
    setValue,
    trigger,
    clearErrors,
  } = useFormContext<UploadFilesValues>();
  const field = useWatch({ control, name: `files.${fieldIndex}` });
  const folderName = useWatch({ control, name: `type` });
  const selected = {
    file: field.file,
    fileId: field.id,
  };

  const nameError = errors.files?.[fieldIndex]?.name?.message;
  const fileError = errors.files?.[fieldIndex]?.file;
  const error = fileError?.message ?? nameError;

  const filePreview = useMemo(
    () =>
      selected.file &&
      ACCEPTED_IMAGE_TYPES.includes(selected.file.type) &&
      URL.createObjectURL(selected.file),
    [selected.file],
  );

  const {
    mutate: uploadTempFileMutation,
    isPending,
    isSuccess,
    isError,
  } = useMutation({
    mutationFn: uploadTempFile.mutation,
    onSuccess: ({ fileId }) => {
      setValue(`files.${fieldIndex}.id`, fileId);
      clearErrors(`files.${fieldIndex}.id`);
    },
    onError: (err) => {
      console.error(err);
      setError(
        `files.${fieldIndex}.file`,
        {
          type: "backend",
          message: t`There was an error, and the file couldn't be uploaded correctly.`,
        },
        { shouldFocus: true },
      );
    },
  });

  useEffect(() => {
    if (error) {
      return;
    }
    if (selected.file.size > MAX_UPLOAD_SIZE) {
      void trigger(`files.${fieldIndex}.file`);
    } else if (!selected.fileId) {
      void trigger(`files.${fieldIndex}.id`);
      uploadTempFileMutation(selected.file);
    }
  }, [
    fieldIndex,
    selected.file,
    selected.fileId,
    error,
    trigger,
    uploadTempFileMutation,
  ]);

  const handleRetry = () => {
    if (fileError?.type === "backend" && selected.file) {
      uploadTempFileMutation(selected.file);
      clearErrors(`files.${fieldIndex}.file`);
    }
  };

  const handleRemove = () => {
    onRemove();
  };

  return (
    <div className="relative flex flex-col gap-2.5 rounded-2xl border border-neutral-400 bg-white p-4 text-neutral-900">
      <IconWrapper
        onClick={handleRemove}
        size="sm"
        as="button"
        type="button"
        className="absolute top-4 right-5 text-red-700"
      >
        <TrashBin2Icon />
      </IconWrapper>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`files.${fieldIndex}.name`}>
          {getDocumentEditNameLabel(folderName)}
        </Label>

        <Input.Root
          id={`files.${fieldIndex}.name`}
          {...register(`files.${fieldIndex}.name`)}
        >
          <Input.Slot>{DOCUMENT_ICON[folderName]}</Input.Slot>
        </Input.Root>
      </div>

      <UploadedFileCard.Root>
        <UploadedFileCard.Container className={tw(!!fileError && "bg-red-100")}>
          {filePreview &&
            (selected.file?.type.includes("image") ? (
              <UploadedFileCard.Preview
                src={filePreview}
                alt={selected.file.name}
                loading="lazy"
              />
            ) : (
              <div className="aspect-square h-full rounded-md object-cover">
                <DocumentIcon />
              </div>
            ))}

          <div className="flex grow justify-between">
            {selected.file && (
              <UploadedFileCard.Content>
                <UploadedFileCard.Title>
                  {selected.file.name}
                </UploadedFileCard.Title>

                <UploadedFileCard.Description>
                  {formatBytes(selected.file.size, { decimals: 2 })}
                </UploadedFileCard.Description>
              </UploadedFileCard.Content>
            )}

            <UploadedFileCard.Actions>
              {isPending && <Spinner />}

              {isError && (
                <IconWrapper
                  as="button"
                  type="button"
                  size="md"
                  onClick={handleRetry}
                  className="cursor-pointer"
                >
                  <ReloadIcon />
                </IconWrapper>
              )}

              {isSuccess && (
                <IconWrapper
                  size="md"
                  className="rounded-full border-2 border-neutral-600 bg-neutral-500 text-neutral-950"
                >
                  <CheckIcon />
                </IconWrapper>
              )}
            </UploadedFileCard.Actions>
          </div>
        </UploadedFileCard.Container>

        {error && <Message error={error} />}
      </UploadedFileCard.Root>
    </div>
  );
};
